import { useEffect, useState } from 'react';
import { connect, RootStateOrAny } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { useHistory } from 'react-router';
import moment from "moment";

import { Typography, Divider, Table, Collapse, Button, Skeleton } from "antd";
import { CheckSquareOutlined, PrinterOutlined } from '@ant-design/icons';

import { ErrorResponse } from 'utils/types';
import notifier from 'utils/notifier';
import { formatPhoneNumber, toLetters, getDisplayCompanyId } from 'utils/helpers';

import { get as getPortalClaim } from 'store/claim/portal/actions';
import { getAttachments } from 'store/claim/member/actions';

import './index.scss';

const { Title } = Typography;
const { Panel } = Collapse;

interface ClaimConfirmationProps {
  claimConfirmInfo: any,
  setPage(page: string): void,

  getPortalClaim(claimNo: string, companyId: string): any,
  isFetchingPortalClaim: boolean,
  getAttachments(claimNo: string, companyId: string): any,
  isFetchingAttachments: boolean,
}

const ClaimConfirmation = (props: ClaimConfirmationProps) => {

  const history = useHistory();

  const [portalClaimMaster, setPortalClaimMaster] = useState<any>({}); // any type is fine here, portalClaimMaster object is massive.
  const [portalClaimDiags, setPortalClaimDiags] = useState([]);
  const [portalClaimDetails, setPortalClaimDetails] = useState([]);
  const [attachmentList, setAttachmentList] = useState<string[]>([]);

  const getPortalClaimData = () => {
    return props.getPortalClaim(props.claimConfirmInfo.ClaimReferenceNo, props.claimConfirmInfo.CompanyId)
      .then((response: any) => {
        // any type is fine here
        setPortalClaimMaster(response.portalClaimMaster);
        setPortalClaimDiags(response.portalClaimDiags);
        setPortalClaimDetails(response.portalClaimDetails);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const getClaimAttachments = () => {
    return props.getAttachments(props.claimConfirmInfo.ClaimReferenceNo, props.claimConfirmInfo.CompanyId)
      .then((response: string[]) => {
        setAttachmentList(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  useEffect(() => {
    getPortalClaimData();
    getClaimAttachments();
  }, [])

  const diagColumns = [
    {
      title: 'DIAG REF',
      key: 'Index',
      dataIndex: 'index',
      render: (text: any, record: any, index: any) => toLetters(index + 1)
    },
    {
      title: 'DIAGNOSIS CODE',
      key: 'RmiDiagCode',
      dataIndex: 'RmiDiagCode',
    },
    {
      title: 'DESCRIPTION',
      key: 'RmiDiagDesc',
      dataIndex: 'RmiDiagDesc',
    },
  ];

  const procColumns = [
    {
      title: 'SUPPL. INFO',
      key: 'RmiDetailNote',
      dataIndex: 'RmiDetailNote',
    },
    {
      title: <>FROM<br />TO</>,
      key: 'RmiDosFrom',
      dataIndex: 'RmiDosFrom',
      render: (text: string, record: any) => {
        if (record.key === 'summary') return;
        return (
            <>
            {record.RmiTotalTime ? moment(text).format('MM/DD/YYYY [@] h:mm A') : moment(text).format('MM/DD/YYYY')}<br />{record.RmiTotalTime ? moment(record.RmiDosTo).format('MM/DD/YYYY @ h:mm A') : moment(record.RmiDosTo).format('MM/DD/YYYY')}<br/><span>{record.RmiTotalTime ? `${record.RmiTotalTime} minutes` : ''}</span>
            </>
          );
      }
    },
    {
      title: 'POS',
      key: 'RmiPosCode',
      dataIndex: 'RmiPosCode',
    },
    {
      title: 'EMG',
      key: 'RmiEmg',
      dataIndex: 'RmiEmg',
    },
    {
      title: 'CPT',
      key: 'RmiCptCode',
      dataIndex: 'RmiCptCode',
    },
    {
      title: 'MODIFIERS',
      key: 'RmiCptModifier1',
      dataIndex: 'RmiCptModifier1',
      render: (text: string, record: any) => <>{text} {record.RmiCptModifier2} {record.RmiCptModifier3} {record.RmiCptModifier4}</>
    },
    {
      title: 'DIAG REF',
      key: 'RmiDiagPtr',
      dataIndex: 'RmiDiagPtr',
      render: (text: string, record: any) => {
        if (record.key === 'summary') {
          return (
            <div style={{ fontWeight: 'bolder' }}>
              Patient Account
              <br />
              {portalClaimMaster.RmiPatientAcctNo ? portalClaimMaster.RmiPatientAcctNo : <>&nbsp;</>}
            </div>
          );
        }
        return (
          <>
            {text ? toLetters(parseInt(text)) : null}&nbsp;
            {record.RmiDiagPtr2 ? toLetters(parseInt(record.RmiDiagPtr2)) : null}&nbsp;
            {record.RmiDiagPtr3 ? toLetters(parseInt(record.RmiDiagPtr3)) : null}&nbsp;
            {record.RmiDiagPtr4 ? toLetters(parseInt(record.RmiDiagPtr4)) : null}
          </>
        )
      }
    },
    {
      title: 'CHARGES',
      key: 'RmiCharges',
      dataIndex: 'RmiCharges',
      render: (value: number, record: any) => {
        if (record.key === 'summary') {
          return (
            <div style={{ fontWeight: 'bolder' }}>
              Total Charge
              <br />
              {portalClaimMaster.RmiTotalCharge ? `$${parseFloat(portalClaimMaster?.RmiTotalCharge).toFixed(2)}` : '$0.00'}
            </div>
          )
        } else return  value ? `$${value.toFixed(2)}` : null;
      }
    },
    {
      title: 'DAYS OR UNITS',
      key: 'RmiDays',
      dataIndex: 'RmiDays',
      render: (value: number, record: any) => {
        if (record.key === 'summary') {
          return (
            <div style={{ fontWeight: 'bolder' }}>
              Amount Paid
              <br />
              {portalClaimMaster?.RmiAmountPaid ? `$${parseFloat(portalClaimMaster?.RmiAmountPaid).toFixed(2)}` : '$0.00'}
            </div>
          )
        } else return value;
      }
    },
    {
      title: 'PROVIDER NPI',
      key: 'RmiRendProviderNpi',
      dataIndex: 'RmiRendProviderNpi',
      render: (value: string, record: any) => {
        if (record.key === 'summary') {
          return (
            <div style={{ fontWeight: 'bolder' }}>
              Balance
              <br />
              {portalClaimMaster?.RmiBalanceDue ? `$${parseFloat(portalClaimMaster?.RmiBalanceDue).toFixed(2)}` : '$0.00'}
            </div>
          )
        } else return value;
      }
    },
  ];

  const summary = {
    key: 'summary',
    RmiDosFrom: 'summary',
    RmiDiagPtr: 'summary',
    RmiCharges: 'summary',
    RmiDays: 'summary',
    RmiRendProviderNpi: 'summary',
  };

  const onPrint = () => {
    window.print();
  };

  const panelHeader = (
    <div>
      <Title 
        level={5} 
        style={{
          color: 'white',
          fontWeight: 'bolder',
          textAlign: 'center',
        }}
      >
        Claim request has been submitted.
      </Title>
    </div>
  );
  
  return (
    <Collapse 
      className="mt-3" 
      collapsible={'disabled'}
      defaultActiveKey={['1']}
    >
      <Panel
        header={panelHeader} 
        key="1" 
        className="claim-confirm-panel-header"
        showArrow={false}
      >

        <div className="col-lg-24 mt-2 mb-1">
          <div className="btn-wrapper">
            <Button type="primary" className="claim-conf-button" style={{ background: '#1BB294', borderColor: '#1BB294' }} onClick={() => history.push('/quality/overview')}>
              <CheckSquareOutlined /> Done
            </Button>
            &nbsp;
            <Button type="primary" className="claim-conf-button" style={{ background: '#1BB294', borderColor: '#1BB294' }} onClick={() => props.setPage('claim-detail')}>
              Another Claim for Same Member
            </Button>
            &nbsp;
            <Button type="primary" className="claim-conf-button" style={{ background: '#1BB294', borderColor: '#1BB294' }} onClick={() => props.setPage('start')}>
              Another Claim for Different Member
            </Button>
            &nbsp;
            <Button type="primary" className="claim-conf-button" style={{ background: '#1BB294', borderColor: '#1BB294' }} onClick={onPrint}>
              <PrinterOutlined /> Print
            </Button>
          </div>
          <Title level={5}>
            Claim Reference Number <span style={{ color: 'green' }}>{getDisplayCompanyId(portalClaimMaster.RmiCompanyId)} - {portalClaimMaster.RmiClaimReferenceNo}</span>
          </Title>
        </div>

      {props.isFetchingPortalClaim && props.isFetchingAttachments ? (
      <>
       <Skeleton 
          active
          paragraph={{ rows: 4 }}
       />
       <br />
       <Skeleton
          avatar 
          active
          paragraph={{ rows: 8 }}
       />
       <br />
       <Skeleton 
          active
          paragraph={{ rows: 4 }}
       />
      </>
    ) : (
      <div className="print-claim-info">
      <div className="row flex-lg-wrap justify-content-start mb-3 ml-0 mt-3">
        <div className="col-lg-12 col-md-12 mb-4 col-print-6">
          <Title level={5}>
            Patient's Information
          </Title>
          <Divider />
          <table className="test"  style={{ textAlign: "left" }}>
            <tbody>
              <tr>
                <th>Claim Ref No:</th>
                <td>{getDisplayCompanyId(portalClaimMaster.RmiCompanyId)} - {portalClaimMaster.RmiClaimReferenceNo}</td>
              </tr>
              <tr>
                <th>Name:</th>
                <td>{portalClaimMaster.RmiPatientName}</td>
              </tr>
              <tr>
                <th>Address:</th>
                <td>{portalClaimMaster.RmiPatientAddress}</td>
              </tr>
              <tr>
                <th>City/State/Zip:</th>
                <td>
                  {portalClaimMaster.RmiPatientCity}, {portalClaimMaster.RmiPatientState} {portalClaimMaster.RmiPatientZip}
                </td>
              </tr>
              <tr>
                <th>Phone:</th>
                <td>{formatPhoneNumber(portalClaimMaster.RmiPatientPhone)}</td>
              </tr>
              <tr>
                <th>Birth Date:</th>
                <td>{moment(portalClaimMaster.RmiPatientBirthDate).format('MM/DD/YYYY')}</td>
              </tr>
              <tr>
                <th>Sex:</th>
                <td>{portalClaimMaster.RmiInsuredSex}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="col-lg-12 col-md-12 mb-4 col-print-6">
          <Title level={5}>Patient's Condition Related To</Title>
          <Divider />
          <table className="test"  style={{ textAlign: "left" }}>
            <tbody>
              <tr key="employment">
                <th>Employment:</th>
                <td>{portalClaimMaster.RmiEmploymentRelated}</td>
              </tr>
              <tr key="unableToWork">
                <th>Unable to work in current occupation:</th>
                <td>
                  {portalClaimMaster.RmiDateNoWorkFrom ? 'From: ' + moment(portalClaimMaster.RmiDateNoWorkFrom).format('MM/DD/YYYY') : null}
                  &nbsp;&nbsp;{portalClaimMaster.RmiDateNoWorkTo ? ` To: ${moment(portalClaimMaster.RmiDateNoWorkTo).format('MM/DD/YYYY')}` : null}
                </td>
              </tr>
              <tr key="auto">
                <th>Auto Accident:</th>
                <td>{portalClaimMaster.RmiAutoRelated}</td>
              </tr>
              <tr key="autoPlace">
                <th>Place (State):</th>
                <td>
                  {portalClaimMaster.RmiAutoAccidentState}
                </td>
              </tr>
              <tr key="phone">
                <th>Other Accident:</th>
                <td>{portalClaimMaster.RmiOtherRelated}</td>
              </tr>
            </tbody>
          </table>
        </div>


        <div className="col-lg-12 col-md-12 col-print-6">
          <Title level={5}>
            Insured's Information
          </Title>
          <Divider />
          <table className="test"  style={{ textAlign: "left" }}>
            <tbody>
              <tr>
                <th>ID Number:</th>
                <td>{portalClaimMaster.RmiInsuredId}</td>
              </tr>
              <tr>
                <th>Name:</th>
                <td>{portalClaimMaster.RmiInsuredName}</td>
              </tr>
              <tr>
                <th>Address:</th>
                <td>{portalClaimMaster.RmiInsuredAddress}</td>
              </tr>
              <tr>
                <th>City/State/Zip:</th>
                <td>
                  {portalClaimMaster.RmiInsuredCity}, {portalClaimMaster.RmiInsuredState} {portalClaimMaster.RmiInsuredZip}
                </td>
              </tr>
              <tr>
                <th>Phone:</th>
                <td>{formatPhoneNumber(portalClaimMaster.RmiInsuredPhone)}</td>
              </tr>
              <tr>
                <th>Birth Date:</th>
                <td>{moment(portalClaimMaster.RmiPatientBirthDate).format('MM/DD/YYYY')}</td>
              </tr>
              <tr>
                <th>Patient Relationship:</th>
                <td>{portalClaimMaster.RmiPatRel}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="col-lg-12 col-md-12 col-print-6">
          <Title level={5}>
            Other Insurance
          </Title>
          <Divider />
          <table className="test"  style={{ textAlign: "left" }}>
            <tbody>
              <tr>
                <th>Other Insured's Name:</th>
                <td>{portalClaimMaster.RmiOtherInsuredName}</td>
              </tr>
              <tr>
                <th>Other Insured's Policy or Group No.:</th>
                <td>{portalClaimMaster.RmiOtherInsuredPolicy}</td>
              </tr>
              <tr>
                <th>Insurance Plan or Program Name:</th>
                <td>
                  {portalClaimMaster.RmiOtherInsuredPlan}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <Divider />

      <div className="col-lg-24 col-md-24 mb-4">
        <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              <th>Date of Current Illness, Injury, or Pregnancy (LMP)</th>
              <th>Referring Provider</th>
              <th>Hospitalization From</th>
              <th>Resubmission Code</th>
              <th>Outside Lab?</th>
            </tr>
          </thead>
          <tbody>
            <tr key="1">
              <td>{portalClaimMaster.RmiDateOfCurrent ? moment(portalClaimMaster.RmiDateOfCurrent).format('MM/DD/YYYY') : null}</td>
              <td>{portalClaimMaster.RmiReferringProvider}</td>
              <td>{portalClaimMaster.RmiHospitalizedFrom ? moment(portalClaimMaster.RmiHospitalizedFrom).format('MM/DD/YYYY') : null}</td>
              <td>{portalClaimMaster.RmiMedicaidResubCode}</td>
              <td>{portalClaimMaster.RmiOutsideLab}</td>
            </tr>
          </tbody>
        </table>

        <br />

        <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              <th>Other Date</th>
              <th>NPI</th>
              <th>Hospitalization To</th>
              <th>Original Ref No</th>
              <th>Prior Auth No</th>
            </tr>
          </thead>
          <tbody>
            <tr key="1">
              <td>{portalClaimMaster.RmiDateOfSimilar ? moment(portalClaimMaster.RmiDateOfSimilar).format('MM/DD/YYYY') : null}</td>
              <td>{portalClaimMaster.RmiReferringProviderNpi}</td>
              <td>{portalClaimMaster.RmiHospitalizedTo ? moment(portalClaimMaster.RmiHospitalizedTo).format('MM/DD/YYYY') : null}</td>
              <td>{portalClaimMaster.RmiMedicaidOrigRefNo}</td>
              <td>{portalClaimMaster.RmiPriorAuthNo}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {portalClaimMaster.RmiClaimNote && (
        <div className="col-lg-24 mb-1 mt-4">
          <Title level={5}>Notes</Title>
            {portalClaimMaster.RmiClaimNote}
            <Divider />
        </div>
      )}

      <Divider />
      <div className="col-lg-24 col-md-24 mb-3">
        <Title level={5}>
          Diagnosis Information
        </Title>
        {(portalClaimDiags.length !== 0 && 
          <Table
            columns={diagColumns}
            dataSource={portalClaimDiags}
            pagination={false}
            rowKey={"RmiDiagCode"}
          />
        )}
      </div>

      <div className="col-lg-24 mb-4">
        <Title level={5}>Procedure Information</Title>
        {portalClaimDetails.length !== 0 && (
          <Table 
            columns={procColumns}
            dataSource={[...portalClaimDetails, summary]}
            pagination={false}
            rowKey={'RmiCptCode'}
          />
        )}
      </div>

      <Title level={5}>
        Attachments
      </Title>
      <ul>
        {attachmentList.map(attachment => <li>{attachment}</li>)}
      </ul>
      <Divider />

      <div className="row flex-lg-wrap justify-content-start mb-3 ml-0 mt-3">
        <div className="col-lg-12 col-md-12 col-print-6">
          <Title level={5}>
            Billing Provider Information
          </Title>
          <table className="test"  style={{ textAlign: "left" }}>
            <tbody>
              <tr>
                <th>NPI:</th>
                <td>{portalClaimMaster.RmiBillingProviderNpi}</td>
              </tr>
              <tr>
                <th>Tax ID:</th>
                <td>{portalClaimMaster.RmiFedTaxId}</td>
              </tr>
              <tr>
                <th>Name:</th>
                <td>{portalClaimMaster.RmiBillingProviderName}</td>
              </tr>
              <tr>
                <th>Address:</th>
                <td>
                  {portalClaimMaster.RmiBillingProviderAddress}
                </td>
              </tr>
              <tr>
                <th>City/State/Zip:</th>
                <td>{portalClaimMaster.RmiBillingProviderCity}, {portalClaimMaster.RmiBillingProviderState} {portalClaimMaster.RmiBillingProviderZip}</td>
              </tr>
              <tr>
                <th>Phone:</th>
                <td>{formatPhoneNumber(portalClaimMaster.RmiBillingProviderPhone)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="col-lg-12 col-md-12 col-print-6">
          <Title level={5}>
            Facility Information
          </Title>
          <table className="test"  style={{ textAlign: "left" }}>
            <tbody>
              <tr>
                <th>NPI:</th>
                <td>{portalClaimMaster.RmiFacilityNpi}</td>
              </tr>
              <tr>
                <th>Name:</th>
                <td>{portalClaimMaster.RmiFacilityName}</td>
              </tr>
              <tr>
                <th>Address:</th>
                <td>{portalClaimMaster.RmiFacilityAddress}</td>
              </tr>
              <tr>
                <th>City/State/Zip:</th>
                <td>
                  {portalClaimMaster.RmiFacilityCity ? portalClaimMaster.RmiFacilityCity + ', ' : ''} {portalClaimMaster.RmiFacilityState} {portalClaimMaster.RmiFacilityZip}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      </div>
    )}
      </Panel>
    </Collapse>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingPortalClaim: store.rootReducer.claimPortal.isFetching,
  isFetchingAttachments: store.rootReducer.claimMember.isFetchingAttachments,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
{
  getPortalClaim,
  getAttachments,
},
  dispatch,
);

export default connect(mapStateToProps, mapDispatchToProps)(ClaimConfirmation);